import { useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { AVATAR_COUNT, getAvatarPath, AVATAR_LABELS } from '@/lib/avatar-utils';
import { cn } from '@/lib/utils';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

interface AvatarSelectionGridProps {
  onClose?: () => void;
}

const AvatarSelectionGrid = ({ onClose }: AvatarSelectionGridProps = {}) => {
  const { updateAvatar, skipAvatar } = useAuth();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (selectedId === null) return;
    setSaving(true);
    await updateAvatar(selectedId);
    setSaving(false);
    onClose?.();
  };

  const handleSkip = async () => {
    await skipAvatar();
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex flex-col md:items-center md:justify-center">
      {/* Mobile: full-screen flex column. Desktop: centered modal. */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col w-full h-full md:h-auto md:max-w-[480px] md:max-h-[90vh] md:bg-card md:border md:border-border md:rounded-2xl md:card-elevated"
      >
        {/* Header — fixed at top */}
        <div className="relative text-center px-4 pt-6 pb-4 md:px-6 md:pt-6 md:pb-4 shrink-0">
          {onClose && (
            <button
              onClick={onClose}
              className="absolute left-4 top-6 p-1 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
              aria-label="סגור"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-xl font-bold mb-1">בחר את הדמות שלך</h2>
          <p className="text-sm text-muted-foreground">האווטאר שיופיע בלוח התוצאות</p>
        </div>

        {/* Grid — fills available space, scrolls if needed */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6">
          <motion.div
            className="grid grid-cols-4 gap-2 py-2"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {Array.from({ length: AVATAR_COUNT }, (_, i) => i + 1).map((id) => (
              <motion.button
                key={id}
                variants={itemVariants}
                onClick={() => setSelectedId(id)}
                className={cn(
                  'relative aspect-square rounded-2xl overflow-hidden transition-all duration-200 focus:outline-none',
                  selectedId === id
                    ? 'border-2 border-accent ring-2 ring-accent/30 scale-[1.03]'
                    : 'border-2 border-transparent hover:border-border'
                )}
                title={AVATAR_LABELS[id]}
                aria-label={AVATAR_LABELS[id]}
                aria-pressed={selectedId === id}
              >
                <img
                  src={getAvatarPath(id)}
                  alt={AVATAR_LABELS[id]}
                  className="w-full h-full rounded-xl object-cover"
                  loading="lazy"
                />
                {selectedId === id && (
                  <div className="absolute inset-0 rounded-xl bg-accent/10 pointer-events-none" />
                )}
              </motion.button>
            ))}
          </motion.div>
        </div>

        {/* Buttons — fixed at bottom */}
        <div className="flex gap-3 px-4 pb-6 pt-3 md:px-6 md:pb-6 shrink-0">
          <Button
            variant="ghost"
            className="flex-1 text-muted-foreground"
            onClick={handleSkip}
            disabled={saving}
          >
            דלג
          </Button>
          <Button
            variant="accent"
            className="flex-1"
            disabled={selectedId === null || saving}
            onClick={handleConfirm}
          >
            {saving ? 'שומר...' : 'בחירה'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default AvatarSelectionGrid;
